import axios from 'axios';

import type {
    MeetingAgenda,
    MeetingAgendaUpdatePayload,
    MeetingCompletionStatus,
    MeetingCompletionUpdateResponse,
    MeetingTranscript,
} from '@/types/review/PostReviewMeeting';

import { api } from '@/api/api';

export default {
    // US 12774 (Mark evaluation as completed) - backed by a real endpoint on ReviewInstanceController.
    async getMeetingStatus(reviewInstanceId: string): Promise<MeetingCompletionStatus> {
        const response = await api.get(`/reviewinstance/${reviewInstanceId}/meeting-status`);
        return response.data;
    },

    // US 12774 (Mark evaluation as completed) - backed by a real endpoint on ReviewInstanceController.
    async completeMeeting(reviewInstanceId: string): Promise<MeetingCompletionUpdateResponse> {
        const response = await api.put(
            `/reviewinstance/${reviewInstanceId}/meeting-status/complete`,
        );
        return response.data;
    },

    // US 12774 (Mark evaluation as completed) - backed by a real endpoint on ReviewInstanceController.
    async uncompleteMeeting(reviewInstanceId: string): Promise<MeetingCompletionUpdateResponse> {
        const response = await api.put(
            `/reviewinstance/${reviewInstanceId}/meeting-status/uncomplete`,
        );
        return response.data;
    },

    // US 12772 (Meeting Agenda Builder) - backed by ManagerAssessmentController, keyed to the cycle.
    // Returns the persisted agenda (real DB ids), or null when none has been generated yet (200 with a null
    // body) — a null result tells the caller to trigger AI generation. The first read creates the agenda
    // from the base plan in the reader's language (the Accept-Language header); the agenda keeps it after.
    async getMeetingAgenda(cycleInstanceId: string): Promise<MeetingAgenda | null> {
        const response = await api.get(`/ManagerAssessment/agenda/${cycleInstanceId}`);
        // No agenda yet → 200 with an empty body, which axios surfaces as "" (not null). Normalize any
        // non-object payload to null so callers — including the auto-generate check — treat it uniformly.
        const data = response.data;
        return data && typeof data === 'object' ? (data as MeetingAgenda) : null;
    },

    // US 12772 (Meeting Agenda Builder) - backed by ManagerAssessmentController, keyed to the cycle.
    // Manual edits to the meeting agenda.
    async updateMeetingAgenda(
        cycleInstanceId: string,
        payload: MeetingAgendaUpdatePayload,
    ): Promise<MeetingAgenda> {
        const response = await api.put(`/ManagerAssessment/agenda/${cycleInstanceId}`, payload);
        return response.data;
    },

    // US 12773 (Transcript + AI recap) - cycle-keyed on ManagerAssessmentController. Returns the uploaded
    // transcript with its AI recap (recap is null until generated). 404 when none uploaded yet → null.
    async getMeetingTranscript(cycleInstanceId: string): Promise<MeetingTranscript | null> {
        try {
            const response = await api.get(`/ManagerAssessment/transcript/${cycleInstanceId}`);
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    },

    // US 12773 (Transcript + AI recap) - stores/replaces the transcript file for a cycle (clears prior recap).
    async uploadMeetingTranscript(cycleInstanceId: string, file: File): Promise<MeetingTranscript> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(
            `/ManagerAssessment/transcript/${cycleInstanceId}`,
            formData,
        );
        return response.data;
    },

    /**
     * The transcript's own text, as a file.
     *
     * A separate call from the one above on purpose: the meeting page loads the transcript's name and
     * recap on every visit, while the words themselves travel only when someone asks. The response is a
     * file, not JSON — the browser hands it straight to a download.
     */
    async downloadMeetingTranscript(cycleInstanceId: string): Promise<Blob> {
        const response = await api.get<Blob>(
            `/ManagerAssessment/transcript/${cycleInstanceId}/file`,
            { responseType: 'blob' },
        );
        return response.data;
    },
};
